/**
 * The versions under the player (docs/SPEC.md §8.7, §10): one line each,
 * newest first — its number, its `say` and its kind, "from version p" where
 * it does not follow the one before it (a restore, or a change made on an
 * older version: the tree's branches) — with **Go back to this version**
 * (a new version, current at once), **Compare** (the player split at the
 * same moment between this version and the one playing) and **Undo** (back
 * to the current version's parent).
 */
import type { VersionLine } from "../api/versions";
import { VERSIONS, fillAll } from "../copy/changes";
import { Button, Card, Pill } from "../ui";

export interface VersionsTreeProps {
  versions: VersionLine[];
  /** The version compared beside the current one. */
  comparing: number | null;
  /** A change is under way or a restore is in flight: nothing goes back. */
  busy: boolean;
  error: string | null;
  onRestore(n: number): void;
  onCompare(n: number | null): void;
}

const say = (s: string, p: Record<string, unknown>) => fillAll(s, p) ?? s;

export function VersionsTree(props: VersionsTreeProps) {
  const lines = [...props.versions].sort((a, b) => b.n - a.n);
  const current = lines.find((v) => v.is_current) ?? null;
  const undo = current?.parent_n ?? null;
  return (
    <Card className="flex flex-col gap-3 p-4 md:p-5" data-testid="versions">
      <div className="flex items-center justify-between gap-2">
        <h2 className="m-0 text-base font-bold text-slate-900">
          {VERSIONS.title}
        </h2>
        {undo !== null ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={props.busy}
            onClick={() => props.onRestore(undo)}
          >
            {VERSIONS.undo}
          </Button>
        ) : null}
      </div>
      {props.error ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {props.error}
        </p>
      ) : null}
      {lines.length === 0 ? (
        <p className="m-0 text-sm text-slate-500">{VERSIONS.empty}</p>
      ) : (
        <ol className="m-0 flex list-none flex-col p-0">
          {lines.map((v, i) => {
            const before = lines[i + 1];
            const branch =
              v.parent_n !== null && (!before || before.n !== v.parent_n);
            const ready = v.state === "ready";
            const comparing = props.comparing === v.n;
            return (
              <li
                key={v.n}
                data-testid={`version-${v.n}`}
                className={[
                  "flex flex-col gap-2 border-t border-slate-100 px-2 py-2 first:border-t-0",
                  v.is_current ? "bg-brand-tint/20" : "",
                  branch ? "border-l-2 border-l-brand-light/60 pl-2" : "",
                ].join(" ")}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold tabular-nums text-slate-900">
                      {say(VERSIONS.version, { n: v.n })}
                    </span>
                    <span className="text-xs text-slate-500">
                      {VERSIONS.kinds[v.kind] ?? v.kind}
                      {branch
                        ? ` · ${say(VERSIONS.fromVersion, { n: v.parent_n })}`
                        : ""}
                    </span>
                    {v.is_current ? (
                      <Pill size="sm" tone="success">
                        {VERSIONS.current}
                      </Pill>
                    ) : null}
                    {v.state === "building" ? (
                      <Pill size="sm" tone="warning">
                        {VERSIONS.building}
                      </Pill>
                    ) : null}
                    {v.state === "failed" ? (
                      <Pill size="sm" tone="danger">
                        {VERSIONS.failed}
                      </Pill>
                    ) : null}
                  </div>
                  <p className="m-0 break-words text-sm text-slate-700">
                    {v.say || (
                      <span className="text-slate-400">{VERSIONS.noSay}</span>
                    )}
                  </p>
                </div>
                {!v.is_current && ready ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={comparing ? "primary" : "ghost"}
                      aria-pressed={comparing}
                      aria-label={`${comparing ? VERSIONS.stopCompare : VERSIONS.compare} · ${say(VERSIONS.version, { n: v.n })}`}
                      onClick={() => props.onCompare(comparing ? null : v.n)}
                    >
                      {comparing ? VERSIONS.stopCompare : VERSIONS.compare}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={props.busy}
                      aria-label={`${VERSIONS.goBack} · ${say(VERSIONS.version, { n: v.n })}`}
                      onClick={() => props.onRestore(v.n)}
                    >
                      {VERSIONS.goBack}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
